import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Button, DialogContent, DialogFooter, DialogHeader, SearchInput } from "@tellescope/ui";
import { PaginatedList, Spinner } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { InboxTemplateSelectorPreview } from "./InboxTemplateSelectorPreview";
import { InboxTemplateSelectorRow } from "./InboxTemplateSelectorRow";
import { InboxTemplateSelectorContextFilters, useInboxTemplateSelector } from "./useInboxTemplateSelector";

export interface InboxTemplateSelectorDialogContentsProps {
  contextFilters?: InboxTemplateSelectorContextFilters;
  onSelectTemplate: (template: TellescopeModelFromName<"templates">) => void;
  onCancel: () => void;
  enableQuery?: boolean;
}

export const InboxTemplateSelectorDialogContents = ({
  contextFilters,
  onSelectTemplate,
  onCancel,
  enableQuery = true,
}: InboxTemplateSelectorDialogContentsProps) => {
  const { t } = useTranslation("messaging");
  const {
    searchQuery,
    setSearchQuery,
    isSearchPending,
    templates,
    isLoading,
    fetchNextPage,
    previewedTemplate,
    setPreviewedTemplateId,
  } = useInboxTemplateSelector({ contextFilters, enableQuery });

  return (
    <DialogContent className="sm:max-w-4xl max-w-[calc(100%-2rem)]" divided>
      <DialogHeader className="px-6 pt-6">{t("Message Templates")}</DialogHeader>
      <div className="space-y-4 px-6 pb-6">
        <div className="w-full">
          <SearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("Search templates...")}
            className="w-full"
          />
        </div>
        <div className="flex gap-4 h-[500px] max-h-[70svh]">
          <PaginatedList
            onBottomReached={fetchNextPage}
            className="w-2/5 min-w-0 overflow-y-auto pr-1"
            scrollOptions={{ type: "hidden-until-scroll" }}
          >
            {isLoading || isSearchPending ? (
              <div className="flex flex-col items-center justify-center gap-2 h-full">
                <Spinner />
                {t("Loading...")}
              </div>
            ) : templates?.length ? (
              templates.map((template) => (
                <InboxTemplateSelectorRow
                  key={template.id}
                  template={template}
                  isPreviewed={template.id === previewedTemplate?.id}
                  onPreview={setPreviewedTemplateId}
                  onSelect={onSelectTemplate}
                />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 h-full text-sm text-muted-foreground">
                {t("No templates to display")}
              </div>
            )}
          </PaginatedList>
          <div className="flex-1 min-w-0 border rounded-md overflow-hidden">
            <InboxTemplateSelectorPreview template={previewedTemplate} />
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button disabled={!previewedTemplate} onClick={() => previewedTemplate && onSelectTemplate(previewedTemplate)}>
          {t("Use template")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
